import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllTeamMembers,
  addTeamMember,
  updateTeamMember,
  deleteTeamMember,
} from "../../../../../services/teamServices";

import {
  extractArray,
  getStats,
  isDemoMode,
  normalizeText,
} from "../utils/teamManagerUtils";

const CHUNK = 50;

export default function useTeamManager() {
  const [teamMembers, setTeamMembers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");

  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [drawer, setDrawer] = useState({
    type: null,
    member: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllTeamMembers();

      const list = extractArray(data).sort((a, b) => {
        const posA = Number(a.position ?? 999);
        const posB = Number(b.position ?? 999);

        if (posA !== posB) return posA - posB;

        return `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
          `${b.nom || ""} ${b.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        );
      });

      setTeamMembers(list);
    } catch (error) {
      console.error("Erreur lors de la récupération des responsables :", error);
      toast.error("Erreur lors de la récupération des responsables.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const stats = useMemo(() => getStats(teamMembers), [teamMembers]);

  const functionOptions = useMemo(() => {
    return Array.from(
      new Set(
        teamMembers
          .map((member) => String(member.fonction || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [teamMembers]);

  const filteredTeamMembers = useMemo(() => {
    const term = normalizeText(query.trim());

    return teamMembers.filter((member) => {
      const searchableText = [
        member.nom,
        member.prenom,
        member.fonction,
        member.position,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesSearch =
        !term || normalizeText(searchableText).includes(term);

      const matchesFunction =
        !functionFilter ||
        String(member.fonction || "").trim() === functionFilter;

      return matchesSearch && matchesFunction;
    });
  }, [teamMembers, query, functionFilter]);

  const visibleTeamMembers = useMemo(() => {
    return filteredTeamMembers.slice(0, visibleCount);
  }, [filteredTeamMembers, visibleCount]);

  const resetFilters = () => {
    setQuery("");
    setFunctionFilter("");
    setVisibleCount(CHUNK);
  };

  const loadMore = () => {
    setVisibleCount((current) =>
      Math.min(current + CHUNK, filteredTeamMembers.length)
    );
  };

  const openCreateDrawer = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "create",
      member: null,
    });
  };

  const openEditDrawer = (member) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "edit",
      member,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      member: null,
    });
  };

  const handleCreateMember = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await addTeamMember(payload);

      toast.success("Responsable créé avec succès.");
      await fetchTeamMembers();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création du responsable :", error);
      toast.error("Erreur lors de la création du responsable.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async (memberId, payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      try {
        await updateTeamMember(memberId, payload);
      } catch {
        await updateTeamMember({
          id: memberId,
          ...payload,
        });
      }

      toast.success("Responsable mis à jour avec succès.");
      await fetchTeamMembers();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification du responsable :", error);
      toast.error("Erreur lors de la modification du responsable.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (member) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!member?.id) return;

    const confirmed = window.confirm(
      `Supprimer le responsable "${member.prenom || ""} ${member.nom || ""}" ?`
    );

    if (!confirmed) return;

    try {
      await deleteTeamMember(member.id);

      setTeamMembers((current) =>
        current.filter((item) => String(item.id) !== String(member.id))
      );

      toast.success("Responsable supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression du responsable :", error);
      toast.error("Erreur lors de la suppression du responsable.");
    }
  };

  return {
    teamMembers,
    loading,
    saving,
    stats,
    query,
    functionFilter,
    functionOptions,
    visibleTeamMembers,
    filteredTeamMembers,
    visibleCount,
    drawer,
    demoMode,

    setQuery,
    setFunctionFilter,
    resetFilters,
    loadMore,
    refreshData: fetchTeamMembers,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateMember,
    handleUpdateMember,
    handleDeleteMember,
  };
}